import { useCallback, useEffect, useState } from 'react'
import { format } from 'date-fns'
import { AlertCircle, CheckCircle2, CheckCheck, RefreshCw, RotateCcw } from 'lucide-react'
import { hasPermission } from '@nexbit/types'
import {
  apiAdminGetPartnerWallet, apiAdminResolvePartnerWallet, apiAdminRetryPartnerWallet, ApiRequestError,
  type PartnerWalletFilter, type PartnerWalletInstruction, type PartnerWalletList,
} from '@/lib/api'
import { useAuth } from '@/contexts/auth'
import { useT } from '@/contexts/language'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

/**
 * Money a seamless partner has not received — the screen an operator opens after the Telegram
 * alert. Every row is an instruction the system tried to send (a payout, a refund, a reversal) and
 * could not; "ส่งใหม่" tries again with a fresh retry budget, "ปิดเรื่อง" records that it was settled
 * with the partner by hand. Both move or declare money, so both ask for a code from the group.
 */

const FILTERS: PartnerWalletFilter[] = ['attention', 'failed', 'retrying', 'all']

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: PartnerWalletList }

type Dict = ReturnType<typeof useT>['partnerWallet']

function filterLabel(t: Dict, f: PartnerWalletFilter): string {
  return { attention: t.filterAttention, failed: t.filterFailed, retrying: t.filterRetrying, all: t.filterAll }[f]
}

function kindLabel(t: Dict, k: PartnerWalletInstruction['kind']): string {
  return { credit: t.kindCredit, rollback: t.kindRollback, settle: t.kindSettle }[k]
}

/** What a person should read: a pending row that has been tried is "retrying", not "queued". */
function statusOf(row: PartnerWalletInstruction): 'failed' | 'retrying' | 'done' | 'cancelled' | 'pending' {
  if (row.status === 'pending') return row.attempts > 0 ? 'retrying' : 'pending'
  return row.status
}

const STATUS_VARIANT = {
  failed: 'destructive', retrying: 'warning', done: 'success', cancelled: 'outline', pending: 'outline',
} as const

function statusLabel(t: Dict, s: ReturnType<typeof statusOf>): string {
  return {
    failed: t.statusFailed, retrying: t.statusRetrying, done: t.statusDone,
    cancelled: t.statusCancelled, pending: t.statusPending,
  }[s]
}

function when(iso: string): string {
  return format(new Date(iso), 'dd/MM HH:mm:ss')
}

function InstructionRow({ row, t, canAct, onRetry, onResolve }: {
  row: PartnerWalletInstruction
  t: Dict
  canAct: boolean
  onRetry: (r: PartnerWalletInstruction) => void
  onResolve: (r: PartnerWalletInstruction) => void
}) {
  const s = statusOf(row)
  const refLabel = row.reference.type === 'draw' ? t.drawRef : t.roundRef
  return (
    <TableRow>
      <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">{when(row.createdAt)}</TableCell>
      <TableCell className="text-xs whitespace-nowrap">{kindLabel(t, row.kind)}</TableCell>
      <TableCell>
        <p className="font-mono text-xs font-semibold">{row.memberUsername}</p>
        <p className="font-mono text-[10px] text-muted-foreground">{row.partnerName}</p>
        {/* What the partner knows the member by — the thing a person reconciling searches for. */}
        {row.externalMemberId && (
          <p className="font-mono text-[10px] text-muted-foreground">ID {row.externalMemberId}</p>
        )}
      </TableCell>
      <TableCell className="font-mono text-xs text-right whitespace-nowrap">
        {Number(row.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </TableCell>
      <TableCell>
        <p className="text-xs">{row.productName ?? row.productCode ?? '—'}</p>
        <p className="font-mono text-[10px] text-muted-foreground">
          {refLabel} {row.reference.number ?? row.reference.id.slice(0, 8)}
        </p>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <Badge variant={STATUS_VARIANT[s]}>{statusLabel(t, s)}</Badge>
        <p className="font-mono text-[10px] text-muted-foreground mt-1">
          {t.attempts(row.attempts)}
          {s === 'retrying' && <> · {t.nextAttempt} {when(row.nextAttemptAt)}</>}
        </p>
      </TableCell>
      <TableCell className="max-w-[18rem]">
        <p className="font-mono text-[11px] text-muted-foreground line-clamp-2 break-words" title={row.lastError ?? undefined}>
          {row.lastError ?? '—'}
        </p>
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {canAct && row.status === 'failed' && (
          <div className="flex justify-end gap-1">
            <Button variant="outline" size="sm" className="h-8 gap-1.5 font-mono text-xs" onClick={() => onRetry(row)}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />{t.retry}
            </Button>
            <Button variant="ghost" size="sm" className="h-8 gap-1.5 font-mono text-xs" onClick={() => onResolve(row)}>
              <CheckCheck className="h-3.5 w-3.5" aria-hidden />{t.resolve}
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}

/** Under the md breakpoint the table is replaced by cards: its action buttons sat off-screen. */
function useNarrow(): boolean {
  const query = '(max-width: 767px)'
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches)
  useEffect(() => {
    const mq = window.matchMedia?.(query)
    if (!mq) return
    const on = () => setNarrow(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return narrow
}

function InstructionCard({ row, t, canAct, onRetry, onResolve }: {
  row: PartnerWalletInstruction
  t: Dict
  canAct: boolean
  onRetry: (r: PartnerWalletInstruction) => void
  onResolve: (r: PartnerWalletInstruction) => void
}) {
  const s = statusOf(row)
  const refLabel = row.reference.type === 'draw' ? t.drawRef : t.roundRef
  return (
    <li className="flex flex-col gap-2 p-4 border-b border-border last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">{kindLabel(t, row.kind)}</p>
          <p className="font-mono text-lg font-semibold leading-tight">
            {Number(row.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
        </div>
        <Badge variant={STATUS_VARIANT[s]}>{statusLabel(t, s)}</Badge>
      </div>
      <div className="font-mono text-xs break-all">
        <p className="font-semibold">{row.memberUsername}</p>
        <p className="text-[11px] text-muted-foreground">
          {row.partnerName}{row.externalMemberId ? ` · ID ${row.externalMemberId}` : ''}
        </p>
      </div>
      <p className="text-xs">
        {row.productName ?? row.productCode ?? '—'} · {refLabel} {row.reference.number ?? row.reference.id.slice(0, 8)}
        <span className="text-muted-foreground"> · {when(row.createdAt)}</span>
      </p>
      <p className="font-mono text-[11px] text-muted-foreground break-words">
        {t.attempts(row.attempts)}
        {s === 'retrying' && <> · {t.nextAttempt} {when(row.nextAttemptAt)}</>}
        {row.lastError && <> — {row.lastError}</>}
      </p>
      {canAct && row.status === 'failed' && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" className="h-11 gap-1.5 font-mono text-xs" onClick={() => onRetry(row)}>
            <RotateCcw className="h-4 w-4" aria-hidden />{t.retry}
          </Button>
          <Button variant="ghost" className="h-11 gap-1.5 font-mono text-xs border border-border" onClick={() => onResolve(row)}>
            <CheckCheck className="h-4 w-4" aria-hidden />{t.resolve}
          </Button>
        </div>
      )}
    </li>
  )
}

function Loading() {
  return (
    <div className="flex flex-col" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4 border-b border-border last:border-0">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-16 ml-auto" />
          <Skeleton className="h-6 w-24" />
        </div>
      ))}
    </div>
  )
}

export default function AdminPartnerWalletPage() {
  const t = useT().partnerWallet
  const { permissions } = useAuth()
  const canAct = hasPermission(permissions, 'partner-wallet:retry')

  const narrow = useNarrow()
  const [filter, setFilter] = useState<PartnerWalletFilter>('attention')
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [notice, setNotice] = useState<string | null>(null)
  const [retryTarget, setRetryTarget] = useState<PartnerWalletInstruction | null>(null)
  // Resolve is two steps: the note first (it is part of what the code is bound to), then the code.
  const [resolveTarget, setResolveTarget] = useState<{ row: PartnerWalletInstruction; note: string; stage: 'note' | 'otp' } | null>(null)

  const load = useCallback(async (f: PartnerWalletFilter) => {
    setState({ status: 'loading' })
    try {
      setState({ status: 'ok', data: await apiAdminGetPartnerWallet(f) })
    } catch (err) {
      setState({ status: 'error', message: err instanceof ApiRequestError ? err.message : 'โหลดไม่สำเร็จ' })
    }
  }, [])

  useEffect(() => { void load(filter) }, [load, filter])

  const done = (message: string) => { setNotice(message); void load(filter) }
  const summary = state.status === 'ok' ? state.data.summary : null
  const kindText = (r: PartnerWalletInstruction) => kindLabel(t, r.kind)
  const amountText = (r: PartnerWalletInstruction) => Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })

  return (
    <div className="flex flex-col gap-4 p-4 max-w-6xl mx-auto">
      <header className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{t.title}</h1>
          {summary && (
            <div className="flex items-center gap-2">
              <Badge variant={summary.failed > 0 ? 'destructive' : 'outline'}>{t.summaryFailed(summary.failed)}</Badge>
              <Badge variant={summary.retrying > 0 ? 'warning' : 'outline'}>{t.summaryRetrying(summary.retrying)}</Badge>
            </div>
          )}
          <Button
            variant="ghost" size="sm" className="ml-auto h-8 gap-1.5 font-mono text-xs"
            onClick={() => void load(filter)} disabled={state.status === 'loading'}
          >
            <RefreshCw className={'h-3.5 w-3.5' + (state.status === 'loading' ? ' animate-spin' : '')} aria-hidden />
            {t.reload}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground max-w-prose">{t.subtitle}</p>
      </header>

      <div className="flex flex-wrap gap-1" role="group" aria-label={t.title}>
        {FILTERS.map((f) => (
          <Button
            key={f} size="sm" variant={filter === f ? 'default' : 'outline'} aria-pressed={filter === f}
            className="h-8 font-mono text-xs" onClick={() => setFilter(f)}
          >
            {filterLabel(t, f)}
          </Button>
        ))}
      </div>

      {notice && (
        <div className="flex items-center gap-2 rounded-[var(--radius)] border border-success/40 bg-card p-3" role="status">
          <CheckCircle2 className="h-4 w-4 text-success shrink-0" aria-hidden />
          <p className="text-sm">{notice}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
        {state.status === 'loading' && <Loading />}

        {state.status === 'error' && (
          <div className="flex items-center gap-3 p-4" role="alert">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
            <p className="font-mono text-xs text-destructive">{state.message}</p>
            <Button variant="outline" size="sm" className="ml-auto h-8 font-mono text-xs" onClick={() => void load(filter)}>
              {t.reload}
            </Button>
          </div>
        )}

        {state.status === 'ok' && state.data.items.length === 0 && (
          <div className="py-12 px-4 text-center">
            <p className="font-mono text-sm text-muted-foreground">
              {filter === 'attention' ? t.empty : t.emptyFiltered}
            </p>
          </div>
        )}

        {state.status === 'ok' && state.data.items.length > 0 && narrow && (
          <ul>
            {state.data.items.map((row) => (
              <InstructionCard
                key={row.id} row={row} t={t} canAct={canAct}
                onRetry={(r) => { setNotice(null); setRetryTarget(r) }}
                onResolve={(r) => { setNotice(null); setResolveTarget({ row: r, note: '', stage: 'note' }) }}
              />
            ))}
          </ul>
        )}

        {state.status === 'ok' && state.data.items.length > 0 && !narrow && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.colTime}</TableHead>
                  <TableHead>{t.colKind}</TableHead>
                  <TableHead>{t.colMember}</TableHead>
                  <TableHead className="text-right">{t.colAmount}</TableHead>
                  <TableHead>{t.colRef}</TableHead>
                  <TableHead>{t.colStatus}</TableHead>
                  <TableHead>{t.colError}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.data.items.map((row) => (
                  <InstructionRow
                    key={row.id} row={row} t={t} canAct={canAct}
                    onRetry={(r) => { setNotice(null); setRetryTarget(r) }}
                    onResolve={(r) => { setNotice(null); setResolveTarget({ row: r, note: '', stage: 'note' }) }}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {retryTarget && (
        <OtpConfirmDialog
          purpose="partner-wallet.retry"
          payload={{ params: { id: retryTarget.id } }}
          title={t.retryTitle}
          description={t.retryDescription(kindText(retryTarget), amountText(retryTarget), retryTarget.memberUsername)}
          confirmLabel={t.retryConfirm}
          onConfirm={async (otp) => { await apiAdminRetryPartnerWallet(retryTarget.id, otp) }}
          onDone={() => { setRetryTarget(null); done(t.doneRetry) }}
          onClose={() => setRetryTarget(null)}
        />
      )}

      {resolveTarget?.stage === 'note' && (
        <Dialog open onOpenChange={(open) => { if (!open) setResolveTarget(null) }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t.resolveTitle}</DialogTitle>
              <DialogDescription>
                {t.resolveDescription(kindText(resolveTarget.row), amountText(resolveTarget.row), resolveTarget.row.memberUsername)}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="resolve-note">{t.resolveNoteLabel}</Label>
              <Input
                id="resolve-note" value={resolveTarget.note} maxLength={300} autoFocus
                placeholder={t.resolveNoteHint}
                onChange={(e) => setResolveTarget({ ...resolveTarget, note: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button
                disabled={resolveTarget.note.trim().length < 3}
                onClick={() => setResolveTarget({ ...resolveTarget, note: resolveTarget.note.trim(), stage: 'otp' })}
              >
                {t.resolveNext}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {resolveTarget?.stage === 'otp' && (
        <OtpConfirmDialog
          purpose="partner-wallet.resolve"
          payload={{ params: { id: resolveTarget.row.id }, body: { note: resolveTarget.note } }}
          title={t.resolveTitle}
          description={`${t.resolveDescription(kindText(resolveTarget.row), amountText(resolveTarget.row), resolveTarget.row.memberUsername)} — ${resolveTarget.note}`}
          confirmLabel={t.resolveConfirm}
          danger
          onConfirm={async (otp) => { await apiAdminResolvePartnerWallet(resolveTarget.row.id, resolveTarget.note, otp) }}
          onDone={() => { setResolveTarget(null); done(t.doneResolve) }}
          onClose={() => setResolveTarget(null)}
        />
      )}
    </div>
  )
}
